"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";
import { Calendar, Heart, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import type { Gift, GiftTimelineEvent } from "@/types";
import { cn } from "@/lib/utils";
import type { TemplateConfig } from "@/lib/templates";
import {
  getExperienceData,
  type TimelineExperienceData,
} from "@/data/experienceDataSchema";

export type TimelineSectionVariant = "default" | "spotify";

interface TimelinePalette {
  accent: string;
  accentRgb: string;
}

// Quando a seção é renderizada dentro do wizard, o caller passa esses
// handlers para ativar os "lapisinhos" sobrepostos. Em produção (página
// publicada) a prop fica undefined e a seção renderiza normal.
export interface TimelineEditTargets {
  onEditTitle: () => void;
  onEditSubtitle: () => void;
  onEditEvent: (index: number) => void;
  onRemoveEvent: (index: number) => void;
  onAddEvent: () => void;
}

interface TimelineSectionProps {
  gift: Partial<Gift>;
  variant: TimelineSectionVariant;
  cfg?: TemplateConfig;
  palette?: TimelinePalette;
  className?: string;
  editTargets?: TimelineEditTargets;
}

interface DisplayEvent {
  key: string;
  title: string;
  description: string | null;
  dateLabel: string | null;
  isFallback: boolean;
  // Quando o evento veio dos dados editáveis do wizard, mantemos o índice
  // original para que os lápis devolvam a posição correta ao caller.
  sourceIndex?: number;
}

const FALLBACK_EVENTS: DisplayEvent[] = [
  {
    key: "fallback-1",
    title: "O primeiro olhar",
    description:
      "O começo de uma história que merecia ser lembrada para sempre.",
    dateLabel: null,
    isFallback: true,
  },
  {
    key: "fallback-2",
    title: "Um momento especial",
    description: "Pequenos detalhes que viraram grandes memórias.",
    dateLabel: null,
    isFallback: true,
  },
  {
    key: "fallback-3",
    title: "Hoje",
    description: "Mais um capítulo bonito dessa história de amor.",
    dateLabel: null,
    isFallback: true,
  },
];

function formatDateLabel(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(parsed);
  } catch {
    return parsed.toISOString().slice(0, 10);
  }
}

function normalizeEvents(events?: GiftTimelineEvent[]): DisplayEvent[] {
  if (!events || events.length === 0) return FALLBACK_EVENTS;
  return events.map((evt, index) => ({
    key: evt.id || `evt-${index}`,
    title: evt.title?.trim() || "Um momento especial",
    description: evt.description?.trim() ? evt.description.trim() : null,
    dateLabel: formatDateLabel(evt.eventDate),
    isFallback: false,
  }));
}

// Eventos vindos da personalização do wizard (experienceData.timeline.events).
// Sempre prioritários sobre gift.timelineEvents; só caímos para o fallback
// quando o usuário não personalizou e o gift também não tem eventos antigos.
function normalizeCustomEvents(
  events: TimelineExperienceData["events"],
  // No modo edição, NUNCA filtramos — eventos vazios precisam aparecer para
  // o usuário poder editar/remover. Em produção, eventos vazios somem.
  keepEmpty = false
): DisplayEvent[] | null {
  if (!events || events.length === 0) return null;
  const result: DisplayEvent[] = [];
  for (let i = 0; i < events.length; i++) {
    const evt = events[i] ?? {};
    const title = evt.title?.trim();
    const description = evt.description?.trim();
    const dateLabel = formatDateLabel(evt.date);
    if (!keepEmpty && !title && !description && !dateLabel) continue;
    result.push({
      key: `custom-${i}`,
      title: title || "Um momento especial",
      description: description ? description : null,
      dateLabel,
      isFallback: false,
      sourceIndex: i,
    });
  }
  return result.length > 0 ? result : null;
}

export function TimelineSection({
  gift,
  variant,
  cfg,
  palette,
  className,
  editTargets,
}: TimelineSectionProps) {
  const customData = useMemo(
    () => getExperienceData<TimelineExperienceData>(gift, "timeline"),
    [gift]
  );
  const editing = Boolean(editTargets);
  const events = useMemo(() => {
    // No modo edição, sempre mostramos a lista vinda do wizard (mesmo com
    // itens vazios) — assim o usuário consegue editar/remover qualquer um.
    const custom = normalizeCustomEvents(customData?.events, editing);
    if (custom) return custom;
    if (editing) return [];
    return normalizeEvents(gift.timelineEvents);
  }, [customData, gift.timelineEvents, editing]);
  const customTitle = customData?.title?.trim();
  const customSubtitle = customData?.subtitle?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        events={events}
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        className={className}
        title={customTitle}
        subtitle={customSubtitle}
      />
    );
  }

  return (
    <DefaultVariant
      events={events}
      cfg={cfg}
      className={className}
      title={customTitle}
      subtitle={customSubtitle}
      editTargets={editTargets}
    />
  );
}

// ─── Variante default — clara, romântica, premium ──────────────────────────

interface DefaultVariantProps {
  events: DisplayEvent[];
  cfg?: TemplateConfig;
  className?: string;
  title?: string;
  subtitle?: string;
  editTargets?: TimelineEditTargets;
}

function DefaultVariant({
  events,
  cfg,
  className,
  title,
  subtitle,
  editTargets,
}: DefaultVariantProps) {
  const accentHex = cfg?.accentHex ?? "#E11D48";
  const accentSecondary = cfg?.accentSecondary ?? "#881337";

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "timeline-section-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: `${accentHex}33`,
      }}
    >
      <DecorativeFlare variant="default" accent={accentHex} />

      <div className="relative z-10 flex flex-col gap-6 p-5 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] shadow-sm backdrop-blur"
            style={{ color: accentSecondary }}
          >
            <Heart
              className="h-3 w-3"
              style={{ color: accentHex }}
              fill="currentColor"
            />
            Nossa linha do tempo
          </span>
          <div className="relative inline-flex items-start gap-1.5">
            <h2
              className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
              style={{ fontFamily: "var(--font-serif, serif)" }}
            >
              {title || "Os momentos que escreveram nossa história"}
            </h2>
            {editTargets && (
              <EditPencilBtn
                label="Editar título"
                accent={accentHex}
                onClick={editTargets.onEditTitle}
              />
            )}
          </div>
          <div className="relative inline-flex items-start gap-1.5 max-w-md">
            <p className="text-sm text-[#1F2937]/70">
              {subtitle ||
                "Cada data marca um capítulo que valeu a pena viver — e que vale ainda mais lembrar."}
            </p>
            {editTargets && (
              <EditPencilBtn
                label="Editar subtítulo"
                accent={accentHex}
                onClick={editTargets.onEditSubtitle}
              />
            )}
          </div>
        </header>

        <ol className="timeline-default-list relative pl-10 md:pl-0">
          <span
            aria-hidden
            className="absolute top-3 bottom-3 w-0.5 rounded-full left-[15px] md:left-1/2 md:-translate-x-1/2"
            style={{
              background: `linear-gradient(to bottom, ${accentHex}00 0%, ${accentHex}55 14%, ${accentHex}99 50%, ${accentHex}55 86%, ${accentHex}00 100%)`,
            }}
          />
          {events.map((event, index) => (
            <DefaultEventItem
              key={event.key}
              event={event}
              index={index}
              accentHex={accentHex}
              accentSecondary={accentSecondary}
              editTargets={editTargets}
            />
          ))}
        </ol>

        {editTargets && (
          <button
            type="button"
            onClick={editTargets.onAddEvent}
            className="self-center inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold transition-opacity hover:opacity-90"
            style={{
              background: `${accentHex}14`,
              color: accentSecondary,
              border: `1.5px dashed ${accentHex}66`,
            }}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={3} />
            Adicionar momento
          </button>
        )}
      </div>
    </motion.section>
  );
}

// Botão pequeno de "lápis" usado pelos editTargets — renderizado apenas
// no modo edição (wizard). Em produção, a prop fica undefined e o botão
// não aparece.
function EditPencilBtn({
  label,
  accent,
  onClick,
}: {
  label: string;
  accent: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={label}
      className="h-5 w-5 inline-flex items-center justify-center rounded-full shrink-0 transition-transform hover:scale-110"
      style={{
        background: "#FFFFFF",
        color: accent,
        boxShadow: `0 2px 8px ${accent}55`,
        border: `1px solid ${accent}33`,
      }}
    >
      <Pencil className="h-2.5 w-2.5" strokeWidth={2.5} />
    </button>
  );
}

interface DefaultEventItemProps {
  event: DisplayEvent;
  index: number;
  accentHex: string;
  accentSecondary: string;
  editTargets?: TimelineEditTargets;
}

function DefaultEventItem({
  event,
  index,
  accentHex,
  accentSecondary,
  editTargets,
}: DefaultEventItemProps) {
  const alignRight = index % 2 === 1;
  const canEdit = editTargets && event.sourceIndex !== undefined;
  return (
    <motion.li
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index, 4) * 0.08 }}
      className={cn(
        "relative pb-8 last:pb-0",
        "md:grid md:grid-cols-2 md:gap-x-12 md:pb-10"
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute top-2 left-[1px] flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-white/80 text-[11px] font-black text-white",
          "md:left-1/2 md:-translate-x-1/2 md:top-3"
        )}
        style={{
          background: `linear-gradient(135deg, ${accentHex} 0%, ${accentSecondary} 100%)`,
          boxShadow: `0 6px 18px ${accentHex}55`,
          fontFamily: "var(--font-serif, serif)",
        }}
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <article
        className={cn(
          "relative rounded-2xl border bg-white/85 p-5 shadow-sm backdrop-blur transition-transform hover:-translate-y-0.5 hover:shadow-md",
          alignRight ? "md:col-start-2" : "md:col-start-1"
        )}
        style={{ borderColor: `${accentHex}26` }}
      >
        {canEdit && (
          <div className="absolute top-2 right-2 flex gap-1">
            <EditPencilBtn
              label={`Editar momento ${(event.sourceIndex ?? 0) + 1}`}
              accent={accentHex}
              onClick={() => editTargets!.onEditEvent(event.sourceIndex!)}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                editTargets!.onRemoveEvent(event.sourceIndex!);
              }}
              aria-label={`Remover momento ${(event.sourceIndex ?? 0) + 1}`}
              className="h-5 w-5 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
              style={{
                background: "#FFFFFF",
                color: "#9CA3AF",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Trash2 className="h-2.5 w-2.5" strokeWidth={2.5} />
            </button>
          </div>
        )}
        {event.dateLabel ? (
          <div
            className="mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{
              background: `${accentHex}14`,
              color: accentSecondary,
            }}
          >
            <Calendar className="h-3 w-3" />
            {event.dateLabel}
          </div>
        ) : null}
        <h3 className="text-base sm:text-lg font-semibold leading-snug text-[#1F2937]">
          {event.title}
        </h3>
        {event.description ? (
          <p className="mt-1.5 text-sm leading-relaxed text-[#1F2937]/75">
            {event.description}
          </p>
        ) : null}
      </article>
    </motion.li>
  );
}

// ─── Variante Spotify — imersiva, escura ───────────────────────────────────

interface SpotifyVariantProps {
  events: DisplayEvent[];
  palette: TimelinePalette;
  className?: string;
  title?: string;
  subtitle?: string;
}

function SpotifyVariant({
  events,
  palette,
  className,
  title,
  subtitle,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "timeline-section-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <DecorativeFlare variant="spotify" accent={palette.accent} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-6">
        <header className="flex flex-col gap-1">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ color: palette.accent }}
          >
            <Heart className="h-3 w-3" fill="currentColor" />
            Nossa linha do tempo
          </span>
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Os momentos que escreveram nossa história"}
          </h3>
          <p className="text-[11px] text-white/55 leading-relaxed sm:text-[12px]">
            {subtitle ||
              "Uma retrospectiva dos capítulos que merecem ficar no replay."}
          </p>
        </header>

        <ol className="timeline-spotify-list relative pl-7">
          <span
            aria-hidden
            className="absolute top-1 bottom-1 left-[11px] w-px rounded-full"
            style={{
              background: `linear-gradient(to bottom, rgba(${palette.accentRgb},0) 0%, rgba(${palette.accentRgb},0.45) 14%, rgba(${palette.accentRgb},0.7) 50%, rgba(${palette.accentRgb},0.45) 86%, rgba(${palette.accentRgb},0) 100%)`,
            }}
          />
          {events.map((event, index) => (
            <SpotifyEventItem
              key={event.key}
              event={event}
              index={index}
              palette={palette}
            />
          ))}
        </ol>
      </div>
    </motion.section>
  );
}

interface SpotifyEventItemProps {
  event: DisplayEvent;
  index: number;
  palette: TimelinePalette;
}

function SpotifyEventItem({ event, index, palette }: SpotifyEventItemProps) {
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.45, delay: Math.min(index, 4) * 0.08 }}
      className="relative pb-5 last:pb-0"
    >
      <span
        aria-hidden
        className="absolute -left-[17px] top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full"
        style={{
          background: palette.accent,
          boxShadow: `0 0 0 3px rgba(${palette.accentRgb},0.18), 0 0 12px rgba(${palette.accentRgb},0.55)`,
        }}
      />
      <article
        className="rounded-xl px-3 py-2.5 backdrop-blur"
        style={{
          background: `rgba(${palette.accentRgb},0.08)`,
          border: `1px solid rgba(${palette.accentRgb},0.2)`,
        }}
      >
        {event.dateLabel ? (
          <p
            className="text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{ color: palette.accent }}
          >
            {event.dateLabel}
          </p>
        ) : null}
        <p className="mt-0.5 text-[13px] font-semibold leading-snug text-white">
          {event.title}
        </p>
        {event.description ? (
          <p className="mt-1 text-[11px] leading-relaxed text-white/70">
            {event.description}
          </p>
        ) : null}
      </article>
    </motion.li>
  );
}

// ─── Decoração ─────────────────────────────────────────────────────────────

function DecorativeFlare({
  variant,
  accent,
}: {
  variant: TimelineSectionVariant;
  accent: string;
}) {
  if (variant === "spotify") {
    return (
      <>
        <Heart
          className="pointer-events-none absolute -top-3 -right-3 h-16 w-16 opacity-10"
          style={{ color: accent }}
          fill="currentColor"
        />
        <Sparkles
          className="pointer-events-none absolute bottom-3 right-4 h-3.5 w-3.5 opacity-40"
          style={{ color: accent }}
        />
      </>
    );
  }
  return (
    <>
      <Heart
        className="pointer-events-none absolute -top-4 -right-4 h-24 w-24 opacity-15"
        style={{ color: accent }}
        fill="currentColor"
      />
      <Sparkles className="pointer-events-none absolute top-5 left-4 h-4 w-4 text-[#F59E0B]/60" />
      <Heart
        className="pointer-events-none absolute -bottom-3 -left-3 h-14 w-14 text-[#F59E0B]/20"
        fill="currentColor"
      />
    </>
  );
}
